/**
 * Payment API Functions
 *
 * API-Funktionen für Stripe Integration.
 */

import { apiClient } from '../api-client';

// ===========================================
// TYPES - STRIPE
// ===========================================

export interface StripeConfigResponse {
  success: boolean;
  publishable_key?: string;
  error?: string;
}

export interface StripeCheckoutCreateRequest {
  amount: number;
  credits?: number;
  package_id?: string;
}

export interface StripeCheckoutCreateResponse {
  success: boolean;
  checkout_url?: string;
  session_id?: string;
  error?: string;
}

export interface StripeCheckoutSuccessRequest {
  session_id: string;
}

export interface StripeCheckoutSuccessResponse {
  success: boolean;
  amount?: number;
  new_balance?: number;
  message?: string;
  error?: string;
}

// ===========================================
// API FUNCTIONS - STRIPE
// ===========================================

/**
 * Lädt die Stripe Publishable Key Konfiguration vom Backend.
 *
 * PUBLIC ENDPOINT - wird beim Laden der Credits-Seite aufgerufen.
 *
 * @returns StripeConfigResponse mit publishable_key
 *
 * @example
 * const config = await getStripeConfig();
 * if (config.success && config.publishable_key) {
 *   const stripe = await loadStripe(config.publishable_key);
 * }
 */
export async function getStripeConfig(): Promise<StripeConfigResponse> {
  try {
    const response = await apiClient.get<StripeConfigResponse>('/api/v2/payment/stripe/config');
    return response.data;
  } catch (error: unknown) {
    console.error('Stripe Config Error:', error);

    // Axios error handling
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: StripeConfigResponse } };
      if (axiosError.response?.data) {
        return axiosError.response.data;
      }
    }

    return {
      success: false,
      error: 'Stripe ist aktuell nicht konfiguriert',
    };
  }
}

/**
 * Erstellt eine Stripe Checkout Session für Guthaben-Aufladung.
 *
 * User wird zur gehosteten Stripe Checkout-Seite weitergeleitet.
 *
 * @param amount - Betrag in EUR (z.B. 25.00)
 * @param options - Optional: credits und package_id für Credit-Käufe
 * @returns StripeCheckoutCreateResponse mit checkout_url
 *
 * @example
 * // EUR Aufladung
 * const result = await createStripeCheckout(25.00);
 *
 * // Credit Paket Kauf
 * const result = await createStripeCheckout(22.99, { credits: 2500, package_id: 'pkg-2500' });
 */
export async function createStripeCheckout(
  amount: number,
  options?: { credits?: number; package_id?: string }
): Promise<StripeCheckoutCreateResponse> {
  try {
    const response = await apiClient.post<StripeCheckoutCreateResponse>(
      '/api/v2/payment/stripe/create-checkout',
      { amount, ...options }
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Stripe Checkout Create Error:', error);

    // Axios error handling
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: StripeCheckoutCreateResponse } };
      if (axiosError.response?.data) {
        return axiosError.response.data;
      }
    }

    return {
      success: false,
      error: 'Fehler bei der Checkout-Erstellung',
    };
  }
}

/**
 * Bestätigt eine erfolgreiche Stripe Checkout-Zahlung und schreibt Credits gut.
 *
 * Wird aufgerufen nachdem User von Stripe Checkout zurückkehrt.
 * Der Backend verifiziert den Status bei Stripe und schreibt Credits gut (idempotent).
 *
 * @param sessionId - Stripe Checkout Session ID
 * @returns StripeCheckoutSuccessResponse mit neuem Guthaben
 *
 * @example
 * // Nach Rückkehr von Stripe Checkout
 * const params = new URLSearchParams(location.search);
 * const sessionId = params.get('session_id');
 * if (sessionId) {
 *   const result = await confirmStripeCheckout(sessionId);
 *   if (result.success) {
 *     toast.success(result.message);
 *   }
 * }
 */
export async function confirmStripeCheckout(
  sessionId: string
): Promise<StripeCheckoutSuccessResponse> {
  try {
    const response = await apiClient.post<StripeCheckoutSuccessResponse>(
      '/api/v2/payment/stripe/checkout-success',
      { session_id: sessionId }
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Stripe Checkout Confirm Error:', error);

    // Axios error handling
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: StripeCheckoutSuccessResponse } };
      if (axiosError.response?.data) {
        return axiosError.response.data;
      }
    }

    return {
      success: false,
      error:
        'Fehler bei der Zahlungsbestätigung. Falls Ihre Zahlung bereits erfolgt ist, kontaktieren Sie bitte den Support.',
    };
  }
}

// ===========================================
// CONSTANTS
// ===========================================

/** Vordefinierte Auflade-Beträge */
export const TOPUP_AMOUNTS = [10, 25, 50, 100] as const;

/** Minimum Auflade-Betrag */
export const MIN_TOPUP_AMOUNT = 5;

/** Maximum Auflade-Betrag */
export const MAX_TOPUP_AMOUNT = 500;
